import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import BeforeAfter from "@/components/BeforeAfter";
import { RevealText, Reveal } from "@/components/RevealText";
import { getCaseStudy } from "@/lib/api";

function Block({ label, title, body }) {
  return (
    <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 border-t border-white/10 py-12 md:py-16">
      <div className="lg:col-span-4">
        <span className="font-mono text-xs tracking-[0.25em] text-[color:var(--accent)] block mb-4">{label}</span>
        <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight leading-none">{title}</h3>
      </div>
      <div className="lg:col-span-7 lg:col-start-6">
        <p className="font-mono text-base md:text-lg text-white/70 leading-relaxed">{body}</p>
      </div>
    </Reveal>
  );
}

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setCs(null);
    setError(false);
    getCaseStudy(slug).then(setCs).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="pt-44 pb-40 max-w-3xl mx-auto px-6 text-center">
        <h1 className="font-display text-5xl uppercase tracking-tighter mb-6">Not found</h1>
        <Link to="/" className="font-mono text-xs tracking-[0.2em] uppercase border border-white/20 px-6 py-4 hover:bg-white hover:text-black transition-colors">Back home</Link>
      </div>
    );
  }
  if (!cs) return <div className="pt-44 pb-40 max-w-3xl mx-auto px-6 font-mono text-white/40">Loading…</div>;

  return (
    <div className="pt-32 md:pt-44" data-testid="case-study-detail-page">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-14 md:pb-20 border-b border-white/10">
        <Link to="/#work" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-white/50 hover:text-white transition-colors mb-10" data-testid="cs-back">
          <ArrowLeft size={14} /> Selected work
        </Link>
        <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mb-6">
          <span className="text-[color:var(--accent)]">{cs.service}</span><span>/</span><span>{cs.client}</span><span>/</span><span>{cs.year}</span>
        </div>
        <h1 className="font-display text-5xl md:text-[7vw] uppercase tracking-tighter leading-[0.85]">
          <RevealText lines={[cs.title]} />
        </h1>
        <p className="font-mono text-base md:text-lg text-white/60 max-w-2xl mt-8">{cs.summary}</p>
      </section>

      {/* Before/After */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <BeforeAfter before={cs.before} after={cs.after} className="aspect-[16/9]" />
        <p className="font-mono text-xs text-white/40 mt-4 text-center">Drag to compare — before and after Liinex.</p>
      </section>

      {/* Metrics */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-8">
        <div className="grid grid-cols-3 border-l border-t border-white/10">
          {cs.metrics.map((m) => (
            <div key={m.l} className="border-r border-b border-white/10 p-6 md:p-10">
              <div className="font-display text-4xl md:text-6xl tracking-tighter text-[color:var(--accent)]">{m.v}</div>
              <div className="font-mono text-[10px] md:text-xs tracking-[0.15em] uppercase text-white/45 mt-2">{m.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Story */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 md:py-16">
        <Block label="01 — Challenge" title="The challenge" body={cs.challenge} />
        <Block label="02 — Solution" title="What we did" body={cs.solution} />
        <Block label="03 — Results" title="The outcome" body={cs.results} />
      </section>

      {/* Gallery */}
      {cs.gallery?.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {cs.gallery.map((g, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="aspect-[4/3] border border-white/10 overflow-hidden">
                  <img src={g} alt={`${cs.title} ${i + 1}`} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.85]">Want results<br />like {cs.client}?</h2>
          <Link to="/contact" className="group inline-flex items-center gap-3 bg-white text-black px-8 py-5 font-mono text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--accent)] hover:text-white transition-colors self-start" data-testid="cs-cta">
            Start a project <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
